import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { useOperationLogs } from "./useOperationLogs";
import { useWhatsAppNotification } from "./useWhatsAppNotification";
import { notificationSound } from "@/lib/notification-sound";
import { reverseStockFromOrder } from "@/lib/stock-reversal";
import { triggerProductionPrint } from "@/lib/public-order-printing";
import { logger } from "@/lib/logger";
import { safeRemoveChannel } from "@/lib/supabase-channel";
import { getOrdersList } from "@/lib/dataGateway";
import type { Database } from "@/integrations/supabase/types";

type Order = Database["public"]["Tables"]["orders"]["Row"];
type OrderStatus = Database["public"]["Enums"]["order_status"];

export interface OrderWithDetails extends Order {
  items: {
    id: string;
    product_id: string | null;
    product_name: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    notes: string | null;
  }[];
  payments: {
    id: string;
    amount: number;
    fee_amount: number | null;
    payment_method: {
      id: string;
      name: string;
    } | null;
  }[];
}

interface UseOrdersListParams {
  status?: OrderStatus | "all";
  dateFrom?: Date;
  dateTo?: Date;
  page?: number;
  pageSize?: number;
}

export function useOrdersList(params: UseOrdersListParams = {}) {
  const { activeRestaurant } = useRestaurantContext();
  const queryClient = useQueryClient();
  const { status = "all", dateFrom, dateTo, page = 1, pageSize = 20 } = params;
  const { logOperation } = useOperationLogs();
  const { notifyCancellation } = useWhatsAppNotification();

  const {
    data: ordersData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      "orders",
      activeRestaurant?.id,
      status,
      dateFrom?.toISOString(),
      dateTo?.toISOString(),
      page,
      pageSize,
    ],
    queryFn: async () => {
      if (!activeRestaurant) return { orders: [], total: 0 };

      // DataGateway: tenta servidor local (GET /api/orders) -> fallback Supabase.
      let dateToIso: string | undefined;
      if (dateTo) {
        const endOfDay = new Date(dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        dateToIso = endOfDay.toISOString();
      }

      const { data, count, error, source } = await getOrdersList<OrderWithDetails>({
        restaurantId: activeRestaurant.id,
        status: status === "all" ? undefined : status,
        dateFrom: dateFrom ? dateFrom.toISOString() : undefined,
        dateTo: dateToIso,
        page,
        pageSize,
      });

      if (import.meta.env.DEV) {
        console.debug(`[useOrdersList] fonte: ${source}`);
      }
      if (error) throw error;

      return {
        orders: (data ?? []) as OrderWithDetails[],
        total: count || 0,
      };
    },
    enabled: !!activeRestaurant,
  });

  // Subscribe to realtime updates for orders
  useEffect(() => {
    if (!activeRestaurant) return;

    const channel = supabase
      .channel("orders-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "orders",
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        (payload) => {
          logger.log("Order realtime update:", payload);
          
          if (payload.eventType === "INSERT") {
            notificationSound.playNewOrder();
            toast.info(`Novo pedido #${(payload.new as any).order_number}`);
          }
          
          // Invalidate and refetch orders
          queryClient.invalidateQueries({ queryKey: ["orders"] });
        }
      )
      .subscribe();

    return () => {
      safeRemoveChannel(channel);
    };
  }, [activeRestaurant, queryClient]);

  const updateStatusMutation = useMutation({
    mutationFn: async ({
      orderId,
      newStatus,
    }: {
      orderId: string;
      newStatus: OrderStatus;
    }) => {
      if (!activeRestaurant) throw new Error("No restaurant");
      
      const order = ordersData?.orders.find(o => o.id === orderId);
      const oldStatus = order?.status;
      
      const { error } = await supabase
        .from("orders")
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq("id", orderId);

      if (error) throw error;

      // If changing from pending to preparing, trigger production print
      if (oldStatus === 'pending' && newStatus === 'preparing' && order) {
        await triggerProductionPrint(orderId, activeRestaurant.id);
        
        // Send WhatsApp notification for order confirmed (only for delivery orders)
        if (order.delivery_address && order.customer_phone) {
          supabase.functions.invoke('send-order-status-notification', {
            body: { order_id: orderId, notification_type: 'order_confirmed' }
          }).catch(err => logger.warn("Failed to send order confirmed notification:", err));
        }
      }

      // If changing to out_for_delivery, send notification
      if (newStatus === 'out_for_delivery' && order?.customer_phone) {
        supabase.functions.invoke('send-order-status-notification', {
          body: { order_id: orderId, notification_type: 'out_for_delivery' }
        }).catch(err => logger.warn("Failed to send out for delivery notification:", err));
      }

      await logOperation("update", "order", orderId, {
        order_number: order?.order_number,
        status_change: { old: oldStatus, new: newStatus },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Status atualizado com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao atualizar status");
    },
  });

  const cancelOrderMutation = useMutation({
    mutationFn: async (orderId: string) => {
      if (!activeRestaurant) throw new Error("No restaurant");
      
      const order = ordersData?.orders.find(o => o.id === orderId);
      
      // First cancel the order
      const { error } = await supabase
        .from("orders")
        .update({ status: "cancelled", updated_at: new Date().toISOString() })
        .eq("id", orderId);

      if (error) throw error;

      await logOperation("cancel", "order", orderId, {
        order_number: order?.order_number,
        total: order?.total,
        items_count: order?.items?.length,
      });

      // Send WhatsApp notification for cancellation
      if (order) {
        notifyCancellation(
          order.order_number,
          Number(order.total),
          order.items?.length || 0
        );
      }
      
      // Then reverse stock deductions
      const stockResult = await reverseStockFromOrder(activeRestaurant.id, orderId);
      
      if (stockResult.reversals.length > 0) {
        logger.log(`Stock reversed for ${stockResult.reversals.length} ingredients`);
      }
      
      return { stockReversed: stockResult.reversals.length, order };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      if (data?.stockReversed > 0) {
        toast.success(`Pedido cancelado! Estoque de ${data.stockReversed} ingrediente(s) estornado.`);
      } else {
        toast.success("Pedido cancelado!");
      }
    },
    onError: () => {
      toast.error("Erro ao cancelar pedido");
    },
  });

  return {
    orders: ordersData?.orders || [],
    totalOrders: ordersData?.total || 0,
    totalPages: Math.ceil((ordersData?.total || 0) / pageSize),
    isLoading,
    refetch,
    updateStatus: updateStatusMutation.mutate,
    cancelOrder: cancelOrderMutation.mutate,
    isUpdating: updateStatusMutation.isPending || cancelOrderMutation.isPending,
  };
}
